import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EventDetailDto, EventStandingDto, EventTeamStandingDto, PublicUser, TerritoryDto } from '@rw/shared';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Page } from '../../components/Layout';
import { EmptyState, QueryView } from '../../components/states';
import { Avatar, Button, Card } from '../../components/ui';
import { invalidateEvents, useClubSearch, useEventByCode, useEventDetail, useEventMap, useLoadedRegion, useRegion, useUserSearch } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { MY_COLOR } from '../../lib/colors';
import { formatKm } from '../../lib/format';
import { useAuth } from '../../stores/auth';
import { toast } from '../../stores/toasts';
import { Sheet } from '../../components/Sheet';
import { GameMap } from '../map/GameMap';
import { MEDAL, RULE_EXPLAIN, RULE_LABEL, SCORING_LABEL, describeRules, eventTiming, qualificationGaps, formatEventValue, inviteLink, TIER_LABEL } from './event-format';
import { locale, t as tr } from '../../i18n';
import { ZaloBotCard } from './ZaloBotCard';

type Tab = 'ranking' | 'teams' | 'map' | 'rules';
const input = 'h-11 w-full rounded-xl border border-ink-700 bg-ink-900 px-3 text-sm outline-none focus:border-volt';

function useEventAction<V>(fn: (v: V) => Promise<unknown>, success?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (success) toast({ kind: 'success', title: tr(success) });
      invalidateEvents(qc);
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Action failed') }, 6000),
  });
}

/** /events/:id, and /e/:code for shared invite links (private events). */
export function EventDetailPage() {
  const { id, code } = useParams<{ id?: string; code?: string }>();
  const byId = useEventDetail(id);
  const byCode = useEventByCode(code);
  const q = code ? byCode : byId;
  const navigate = useNavigate();
  return (
    <Page>
      <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/events'))} className="mb-2 text-sm font-semibold text-ink-300">
        {tr('← Back')}
      </button>
      <QueryView query={q}>{(e) => <EventView e={e} code={code} />}</QueryView>
    </Page>
  );
}

function EventView({ e, code }: { e: EventDetailDto; code?: string }) {
  const me = useAuth((s) => s.user?.id);
  const [tab, setTab] = useState<Tab>('ranking');
  const podium = e.finalized ? e.standings.individual.filter((r) => r.rank <= 3 && r.qualified) : [];
  const open = e.status === 'UPCOMING' || e.status === 'ACTIVE';

  return (
    <div data-testid="event-detail">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest">
        <span className={e.status === 'ACTIVE' ? 'text-volt' : e.status === 'CANCELLED' ? 'text-enemy' : 'text-gold'}>
          {tr(e.status === 'ACTIVE' ? '● Live' : e.status === 'UPCOMING' ? 'Upcoming' : e.status === 'CANCELLED' ? 'Cancelled' : 'Final results')}
        </span>
        {e.official ? <span className="rounded bg-gold/20 px-1.5 text-gold">{tr('Official')}</span> : null}
        {e.visibility === 'PRIVATE' ? <span className="text-ink-400">🔒 {tr('Private')}</span> : null}
      </div>
      <h1 className="font-display text-3xl font-bold" data-testid="event-name">
        {e.name}
      </h1>
      <div className="mt-1 text-sm text-ink-300">
        {eventTiming(e.status, e.startsAt, e.endsAt)} · {runners(e.participants)} · {tr('by {name}', { name: e.organizer.displayName })}
      </div>
      {e.description ? <p className="mt-2 whitespace-pre-line text-sm text-ink-300">{e.description}</p> : null}
      {e.sponsors?.length ? <Sponsors sponsors={e.sponsors} /> : null}
      {e.approval !== 'APPROVED' ? (
        <div className={`mt-3 rounded-xl border px-3 py-2 text-sm ${e.approval === 'PENDING' ? 'border-gold/30 bg-gold/10 text-gold' : 'border-enemy/30 bg-enemy/10 text-enemy'}`} data-testid="event-approval">
          <div className="font-semibold">{tr(e.approval === 'PENDING' ? 'Waiting for Runner Wars to approve this event' : 'Runner Wars did not approve this event')}</div>
          {e.approval === 'PENDING' ? <div className="mt-0.5 text-xs text-ink-300">{tr('Only you can see it for now. You can still edit it; runners can join once it is approved.')}</div> : null}
        </div>
      ) : null}

      <MyEntry e={e} code={code} />

      {podium.length ? (
        <div className="mt-6 grid grid-cols-3 items-end text-center" data-testid="event-podium">
          {([2, 1, 3] as const).map((place) => {
            const p = podium.find((x) => x.rank === place);
            const step = PODIUM_STEP[place];
            return (
              <div key={place} className="min-w-0">
                {p ? (
                  <div className="mb-2 px-1">
                    <div className="flex justify-center">
                      <Avatar name={p.user.displayName} color={p.user.id === me ? MY_COLOR : p.user.color} src={p.user.avatarUrl} size={place === 1 ? 'lg' : 'md'} />
                    </div>
                    <div className="mt-1 truncate text-sm font-semibold">{p.user.displayName}</div>
                    <div className="text-xs text-ink-400">{formatEventValue(e.rule, p.value)}</div>
                  </div>
                ) : null}
                <div className="h-3" style={{ background: step.top, clipPath: 'polygon(8% 0, 92% 0, 100% 100%, 0 100%)' }} />
                <div className={`flex items-center justify-center font-display font-bold text-white ${step.height}`} style={{ background: step.front }}>
                  <span className={place === 1 ? 'text-5xl' : 'text-4xl'}>{place}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="mt-5 grid grid-cols-4 gap-1 rounded-2xl bg-ink-850 p-1">
        {(
          [
            ['ranking', 'Runners'],
            ['teams', 'Teams'],
            ['map', 'Map'],
            ['rules', 'Rules'],
          ] as const
        ).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`event-tab-${t}`}
            className={`h-10 rounded-xl text-xs font-bold uppercase ${tab === t ? 'bg-volt text-ink-950' : 'text-ink-300'}`}
          >
            {tr(label)}
          </button>
        ))}
      </div>

      {tab === 'ranking' ? <Individual e={e} rows={e.standings.individual} me={me} /> : null}
      {tab === 'teams' ? <Teams e={e} rows={e.standings.teams} /> : null}
      {tab === 'map' ? <EventMap e={e} /> : null}
      {tab === 'rules' ? <Rules e={e} /> : null}

      {e.isOrganizer && open ? <OrganizerPanel e={e} /> : null}
    </div>
  );
}

// ───────────── my participation ─────────────

function MyEntry({ e, code }: { e: EventDetailDto; code?: string }) {
  // Team links shared in the Zalo group (?team=…) preselect that team.
  const [params] = useSearchParams();
  const linked = params.get('team');
  const [teamId, setTeamId] = useState<string | null>(e.me?.teamId ?? (e.teamsList.some((t) => t.id === linked) ? linked : null));
  const [confirmLeave, setConfirmLeave] = useState(false);
  const join = useEventAction(() => api(`/competitions/${e.id}/join`, { body: { teamId, code } }), tr("You're in {name}!", { name: e.name }));
  const switchTeam = useEventAction((t: string) => api(`/competitions/${e.id}/team`, { body: { teamId: t } }), 'Team changed');
  const leave = useEventAction(() => api(`/competitions/${e.id}/leave`, { method: 'POST' }), 'You left the event');
  const decline = useEventAction(() => api(`/competitions/${e.id}/decline`, { method: 'POST' }), 'Invitation declined');
  const open = e.status === 'UPCOMING' || e.status === 'ACTIVE';
  const teams = e.teamsList;

  if (e.me) {
    const team = teams.find((t) => t.id === e.me!.teamId);
    const s = e.me.standing;
    return (
      <Card className="mt-4 border-volt/40 bg-volt/5" >
        <div className="flex items-center justify-between gap-3" data-testid="event-my-entry">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-volt">{tr("You're in")}</div>
            {team ? (
              <div className="mt-0.5 flex items-center gap-1.5 text-sm">
                <span className="size-3 rounded-full" style={{ background: team.color }} /> {tr('Team')} <b>{team.name}</b>
              </div>
            ) : null}
            {s ? (
              <div className="mt-1 text-xs text-ink-400">
                {tr('{n} runs', { n: s.runs })} · {formatKm(s.distanceM, 1)} km · {tr('{n} sectors', { n: s.sectors })}
              </div>
            ) : null}
          </div>
          {s ? (
            <div className="text-right">
              <div className="font-display text-2xl font-bold text-volt">{s.value > 0 && s.qualified ? `#${s.rank}` : '–'}</div>
              <div className="text-xs text-ink-300">{formatEventValue(e.rule, s.value)}</div>
              <div className={`text-[11px] ${s.qualified ? 'text-volt' : 'text-ink-500'}`}>{tr(s.qualified ? '✓ Qualified' : 'Not yet qualified')}</div>
              {s.qualified ? null : <div className="text-[11px] text-gold">{qualificationGaps(e.rules, s).join(' · ')}</div>}
            </div>
          ) : null}
        </div>
        {e.status === 'UPCOMING' && teams.length > 1 ? (
          <select
            className={`${input} mt-3`}
            value={e.me.teamId ?? ''}
            onChange={(ev) => switchTeam.mutate(ev.target.value)}
            aria-label={tr('Switch team')}
          >
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        ) : null}
        {open ? (
          confirmLeave ? (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-enemy/10 p-2 text-sm">
              <span className="flex-1">{tr('Leave? Your sectors leave the event map.')}</span>
              <Button size="sm" variant="danger" onClick={() => leave.mutate(undefined)} loading={leave.isPending}>
                {tr('Leave')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmLeave(false)}>
                {tr('Stay')}
              </Button>
            </div>
          ) : (
            <button className="mt-3 text-xs text-ink-500 underline" onClick={() => setConfirmLeave(true)}>
              {tr('Leave event')}
            </button>
          )
        ) : null}
      </Card>
    );
  }

  if (!open) return null;
  const full = !!e.rules.maxParticipants && e.participants >= e.rules.maxParticipants;
  return (
    <Card className="mt-4 space-y-3">
      {e.invited ? <div className="text-sm font-semibold text-gold">{tr("✉️ You've been invited to this event")}</div> : null}
      {teams.length ? (
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-ink-400">{tr('Pick your team')}</div>
          <div className="grid grid-cols-2 gap-2" data-testid="event-team-picker">
            {teams.map((t) => (
              <button
                key={t.id}
                onClick={() => setTeamId(t.id)}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm ${teamId === t.id ? 'border-volt bg-volt/10' : 'border-ink-700'}`}
              >
                <span className="size-3 shrink-0 rounded-full" style={{ background: t.color }} />
                <span className="min-w-0 flex-1 truncate font-semibold">{t.name}</span>
                <span className="text-xs text-ink-500">{runners(t.members)}</span>
              </button>
            ))}
          </div>
          {teams.some((t) => t.club) ? <p className="mt-1 text-[11px] text-ink-500">{tr("Club teams are for that club's members.")}</p> : null}
        </div>
      ) : null}
      <Button className="w-full" disabled={full || (teams.length > 0 && !teamId)} onClick={() => join.mutate(undefined)} loading={join.isPending} data-testid="join-event">
        {tr(full ? 'Event is full' : e.status === 'ACTIVE' ? 'Join now' : 'Join event')}
      </Button>
      {e.status === 'ACTIVE' ? <p className="text-center text-[11px] text-ink-500">{tr('Runs started after you join will count.')}</p> : null}
      {e.invited ? (
        <button className="w-full text-xs text-ink-500 underline" onClick={() => decline.mutate(undefined)}>
          {tr('Decline invitation')}
        </button>
      ) : null}
    </Card>
  );
}

// ───────────── standings ─────────────

function Individual({ e, rows, me }: { e: EventDetailDto; rows: EventStandingDto[]; me?: string }) {
  if (!rows.length) return <EmptyState icon="🏁" title={tr(e.status === 'UPCOMING' ? 'Not started yet' : 'No runners yet')} />;
  return (
    <ol className="mt-3 divide-y divide-ink-800 overflow-hidden rounded-2xl border border-ink-700/80 bg-ink-850" data-testid="event-standings">
      {rows.map((r) => (
        <li key={r.user.id} className={r.user.id === me ? 'bg-volt/10' : ''}>
          <Link to={r.user.id === me ? '/profile' : `/players/${r.user.id}`} className="flex items-center gap-3 px-4 py-3">
            <span className="w-7 text-center font-display text-lg font-bold text-ink-400">{rankLabel(r.rank, r.value, r.qualified)}</span>
            <Avatar name={r.user.displayName} color={r.user.id === me ? MY_COLOR : r.user.color} src={r.user.avatarUrl} size="sm" />
            <span className="min-w-0 flex-1">
              <span className={`block truncate font-semibold ${r.user.id === me ? 'text-volt' : ''}`}>{r.user.displayName}</span>
              <span className="block truncate text-[11px] text-ink-500">
                {r.team ? (
                  <>
                    <span className="mr-1 inline-block size-2 rounded-full" style={{ background: r.team.color }} />
                    {r.team.name} ·{' '}
                  </>
                ) : null}
                {tr('{n} runs', { n: r.runs })} · {formatKm(r.distanceM, 1)} km
              </span>
              <NotQualified e={e} s={r} />
            </span>
            <span className="font-display font-bold">{formatEventValue(e.rule, r.value)}</span>
          </Link>
        </li>
      ))}
    </ol>
  );
}

const runners = (n: number) => tr(n === 1 ? '{n} runner' : '{n} runners', { n });
/** Before anyone scores, every row ties at 0 — show a dash instead of a shared "1". */
const PODIUM_STEP = {
  1: { height: 'h-24', front: '#f5cf45', top: '#fae69c' },
  2: { height: 'h-16', front: '#adb8cb', top: '#dbe0ea' },
  3: { height: 'h-12', front: '#c2a99a', top: '#ddd0c7' },
} as const;

/** Why a runner has no place yet: what they lack against the event's finish rules. */
function NotQualified({ e, s }: { e: EventDetailDto; s: EventStandingDto }) {
  if (s.qualified) return null;
  const gaps = qualificationGaps(e.rules, s);
  return (
    <span className="block text-[11px] text-gold" data-testid="not-qualified">
      {tr('Not qualified')}
      {gaps.length ? `: ${gaps.join(' · ')}` : ''}
    </span>
  );
}

const rankLabel = (rank: number, value: number, qualified = true) => (value > 0 && qualified ? (rank <= 3 ? MEDAL[rank - 1] : rank) : '–');

function Teams({ e, rows }: { e: EventDetailDto; rows: EventTeamStandingDto[] }) {
  const [open, setOpen] = useState<EventTeamStandingDto | null>(null);
  if (!rows.length) return <EmptyState icon="👥" title={tr('No teams')} body={tr('This event is every runner for themselves.')} />;
  return (
    <>
      <p className="mt-3 text-xs text-ink-400">
        {tr('{scoring} of {rule}. Tap a team to see its runners.', { scoring: tr(SCORING_LABEL[e.teamScoring]), rule: tr(RULE_LABEL[e.rule]).toLowerCase() })}
      </p>
      <ol className="mt-2 divide-y divide-ink-800 overflow-hidden rounded-2xl border border-ink-700/80 bg-ink-850" data-testid="event-team-standings">
        {rows.map((r) => (
          <li key={r.team.id} className={e.me?.teamId === r.team.id ? 'bg-volt/10' : ''}>
            <button onClick={() => setOpen(r)} className="flex w-full items-center gap-3 px-4 py-3 text-left" data-testid={`team-row-${r.team.name}`}>
              <span className="w-7 text-center font-display text-lg font-bold text-ink-400">{rankLabel(r.rank, r.value)}</span>
              <span className="size-8 shrink-0 rounded-full ring-2 ring-ink-700" style={{ background: r.team.color }} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{r.team.name}</span>
                <span className="block text-[11px] text-ink-500">{runners(r.members)}</span>
              </span>
              <span className="font-display font-bold">{formatEventValue(e.rule, r.value)}</span>
              <span className="text-ink-500">›</span>
            </button>
          </li>
        ))}
      </ol>
      <Sheet open={!!open} onClose={() => setOpen(null)} label={tr('Team leaderboard')}>
        {open ? <TeamBoard e={e} team={open} /> : null}
      </Sheet>
    </>
  );
}

/** One team's runners, ranked inside the team. */
function TeamBoard({ e, team }: { e: EventDetailDto; team: EventTeamStandingDto }) {
  const me = useAuth((s) => s.user?.id);
  const members = e.standings.individual.filter((s) => s.team?.id === team.team.id);
  return (
    <div data-testid="team-board">
      <div className="flex items-center gap-3">
        <span className="size-10 shrink-0 rounded-full ring-2 ring-ink-700" style={{ background: team.team.color }} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-2xl font-bold">{team.team.name}</div>
          <div className="text-xs text-ink-400">
            {tr('Team rank')} {team.value > 0 ? `#${team.rank}` : '–'} · {runners(team.members)} · {formatEventValue(e.rule, team.value)}
            {e.teamScoring === 'AVERAGE' ? ` ${tr('avg')}` : ''}
          </div>
        </div>
      </div>
      {members.length ? (
        <ol className="mt-4 divide-y divide-ink-800 overflow-hidden rounded-2xl border border-ink-700/80 bg-ink-900">
          {members.map((r) => {
            const rank = members.filter((m) => m.qualified).findIndex((m) => m.value === r.value) + 1;
            return (
              <li key={r.user.id} className={r.user.id === me ? 'bg-volt/10' : ''}>
                <Link to={r.user.id === me ? '/profile' : `/players/${r.user.id}`} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-7 text-center font-display text-lg font-bold text-ink-400">{rankLabel(rank, r.value, r.qualified)}</span>
                  <Avatar name={r.user.displayName} color={r.user.id === me ? MY_COLOR : r.user.color} src={r.user.avatarUrl} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate font-semibold ${r.user.id === me ? 'text-volt' : ''}`}>{r.user.displayName}</span>
                    <span className="block truncate text-[11px] text-ink-500">
                      {r.qualified ? `${tr('Overall #{n}', { n: r.rank })} · ` : ''}{tr('{n} runs', { n: r.runs })} · {formatKm(r.distanceM, 1)} km · {tr('{n} sectors', { n: r.sectors })}
                    </span>
                    <NotQualified e={e} s={r} />
                  </span>
                  <span className="font-display font-bold">{formatEventValue(e.rule, r.value)}</span>
                </Link>
              </li>
            );
          })}
        </ol>
      ) : (
        <EmptyState icon="👥" title={tr('No runners yet')} body={tr('Nobody has picked this team so far.')} />
      )}
    </div>
  );
}

function EventMap({ e }: { e: EventDetailDto }) {
  const { data: regions, region: current } = useRegion();
  const me = useAuth((s) => s.user?.id ?? null);
  const map = useEventMap(e.id);
  const byTeam = e.teamsList.length > 0;
  // The event's cities this viewer can see; the map shows one at a time (the current one if it's among them).
  const cities = (regions ?? []).filter((r) => e.allRegions || (e.regionIds.length ? e.regionIds : [e.regionId]).includes(r.id));
  const [picked, setPicked] = useState<string | null>(null);
  const cityId = picked ?? (cities.some((c) => c.id === current?.id) ? current!.id : cities[0]?.id);
  const region = useLoadedRegion(cityId);
  const owners = useMemo(() => {
    const m = new Map<string, TerritoryDto>();
    for (const t of map.data ?? []) {
      const regionId = t.regionId ?? e.regionId;
      if (regionId !== cityId) continue;
      m.set(t.gridKey, {
        id: t.gridKey,
        regionId,
        gridKey: t.gridKey,
        capturedAt: t.capturedAt,
        owner: { type: 'USER', id: t.owner.id, displayName: t.owner.displayName, color: t.owner.color, club: t.team },
      });
    }
    return m;
  }, [map.data, e.regionId, cityId]);
  if (!region) return <EmptyState icon="🗺️" title={tr('Map unavailable')} body={tr('This event is in another region.')} />;
  const mine = [...owners.values()].filter((t) => (byTeam ? t.owner?.club?.id === e.me?.teamId : t.owner?.id === me)).length;
  return (
    <>
      <p className="mt-3 text-xs text-ink-400">
        {tr("This event's own map — only participants' runs count here.")} {byTeam ? tr('Coloured by team.') : ''}{' '}
        <b className="text-volt">{mine}</b> {tr(byTeam ? 'held by your team' : 'held by you')} · {tr('{n} taken.', { n: owners.size })}
      </p>
      {cities.length > 1 ? (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="event-map-cities">
          {cities.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setPicked(c.id)}
              className={`rounded-full border px-3 py-1 text-xs font-bold ${c.id === cityId ? 'border-volt bg-volt text-ink-950' : 'border-ink-700 text-ink-300'}`}
              data-testid={`event-map-city-${c.id}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className="relative mt-2 h-[55dvh] min-h-80 overflow-hidden rounded-2xl border border-ink-700" data-testid="event-map">
        <GameMap
          key={region.dto.id}
          className="absolute inset-0"
          region={region}
          owners={owners}
          myUserId={me}
          viewMode={byTeam ? 'clubs' : 'runners'}
          myClubId={e.me?.teamId ?? null}
          initialZoom={13}
        />
      </div>
    </>
  );
}

function Rules({ e }: { e: EventDetailDto }) {
  const r = describeRules(e.rules);
  const navigate = useNavigate();
  const { data: regions } = useRegion();
  const cityNames = e.allRegions
    ? tr('All cities')
    : (e.regionIds.length ? e.regionIds : [e.regionId]).map((id) => regions?.find((x) => x.id === id)?.name ?? id).join(', ');
  return (
    <Card className="mt-3 space-y-3 text-sm" >
      {e.isOrganizer && (e.status === 'UPCOMING' || e.status === 'ACTIVE') ? (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-ink-900 p-3">
          <p className="text-xs text-ink-400">
            {tr(e.status === 'UPCOMING' ? 'You can change the rules until the event starts.' : 'Rules are locked while the event is live.')}
          </p>
          <Button size="sm" variant="secondary" onClick={() => navigate(`/events/${e.id}/edit`)} data-testid="edit-event">
            {tr('Edit')}
          </Button>
        </div>
      ) : null}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">{tr('How to win')}</div>
        <div className="mt-1 font-semibold">🏆 {tr(RULE_LABEL[e.rule])}</div>
        <p className="text-ink-300">{tr(RULE_EXPLAIN[e.rule])}</p>
        {e.teamsList.length ? (
          <p className="mt-1 text-ink-300">
            {tr('Teams')}: {tr(SCORING_LABEL[e.teamScoring]).toLowerCase()}. {tr('Teammates never take sectors from each other.')}
          </p>
        ) : null}
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">{tr('Runs that count')}</div>
        <ul className="mt-1 list-inside list-disc text-ink-300" data-testid="event-rules">
          <li>{tr('Validated runs started after you joined, during the event')}</li>
          <li data-testid="event-cities-rule">{tr('In: {cities}', { cities: cityNames })}</li>
          {r.runs.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </div>
      {r.finish.length ? (
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">{tr('To qualify for awards')}</div>
          <ul className="mt-1 list-inside list-disc text-ink-300">
            {r.finish.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="text-xs text-ink-500">
        {new Date(e.startsAt).toLocaleString(locale())} → {new Date(e.endsAt).toLocaleString(locale())}
        {e.rules.maxParticipants ? ` · ${tr('max {n} runners', { n: e.rules.maxParticipants })}` : ''}
      </div>
      <div className="text-xs text-ink-500">{tr('Organisers')}: {e.organizers.map((o) => o.displayName).join(', ')}</div>
      <Link to="/guide#su-kien" className="block text-sm font-semibold text-volt" data-testid="events-guide-link">
        {tr('How events, teams and qualifying work')} ›
      </Link>
    </Card>
  );
}

// ───────────── organiser tools ─────────────

function OrganizerPanel({ e }: { e: EventDetailDto }) {
  const [teamName, setTeamName] = useState('');
  const [clubQ, setClubQ] = useState('');
  const [userQ, setUserQ] = useState('');
  const [confirm, setConfirm] = useState<'end' | 'cancel' | null>(null);
  const clubs = useClubSearch(clubQ);
  const users = useUserSearch(userQ);
  const addTeam = useEventAction((body: { name?: string; clubId?: string }) => api(`/competitions/${e.id}/teams`, { body }), 'Team added');
  const removeTeam = useEventAction((teamId: string) => api(`/competitions/${e.id}/teams/${teamId}`, { method: 'DELETE' }), 'Team removed');
  const move = useEventAction(
    ({ userId, teamId }: { userId: string; teamId: string }) => api(`/competitions/${e.id}/participants/${userId}`, { method: 'PATCH', body: { teamId } }),
    'Runner moved',
  );
  const invite = useEventAction((userId: string) => api(`/competitions/${e.id}/invites`, { body: { userId } }), 'Invitation sent');
  const addOrg = useEventAction((userId: string) => api(`/competitions/${e.id}/organizers`, { body: { userId } }), 'Co-organiser added');
  const end = useEventAction(() => api(`/competitions/${e.id}/end`, { method: 'POST' }), 'Event ended — results are final');
  const cancel = useEventAction(() => api(`/competitions/${e.id}/cancel`, { method: 'POST' }), 'Event cancelled');
  const link = e.inviteCode ? inviteLink(e.inviteCode) : null;

  const share = async () => {
    if (!link) return;
    try {
      if (navigator.share) await navigator.share({ title: tr('Join {name} on Runner Wars', { name: e.name }), url: link });
      else {
        await navigator.clipboard.writeText(link);
        toast({ kind: 'success', title: tr('Invite link copied') });
      }
    } catch {
      /* share cancelled */
    }
  };

  return (
    <div className="mt-8" data-testid="organizer-panel">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest text-gold">{tr('Organiser tools')}</div>
        <Link to={`/events/${e.id}/edit`} className="text-xs font-semibold text-volt" data-testid="edit-event-link">
          {tr('Edit event ›')}
        </Link>
      </div>
      <Card className="space-y-3">
        <div className="text-sm font-semibold">{tr('Invite runners')}</div>
        {link ? (
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg bg-ink-900 px-2 py-2 text-xs text-ink-300" data-testid="event-invite-link">
              {link}
            </code>
            <Button variant="secondary" size="sm" onClick={share} data-testid="share-event">
              {tr('Share')}
            </Button>
          </div>
        ) : null}
        <input value={userQ} onChange={(ev) => setUserQ(ev.target.value)} placeholder={tr('Search runners by name')} className={input} data-testid="event-invite-search" />
        {userQ.trim().length >= 2 ? (
          <ul className="space-y-1">
            {users.data?.map((u) => (
              <UserRow key={u.id} u={u}>
                <Button size="sm" variant="secondary" onClick={() => invite.mutate(u.id)} data-testid={`invite-${u.displayName}`}>
                  {tr('Invite')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => addOrg.mutate(u.id)} title={tr('Make co-organiser')}>
                  + Org
                </Button>
              </UserRow>
            ))}
          </ul>
        ) : null}
      </Card>

      <Card className="mt-3 space-y-3">
        <div className="text-sm font-semibold">{tr('Teams')}</div>
        {e.teamsList.length ? (
          <ul className="space-y-1">
            {e.teamsList.map((t) => (
              <li key={t.id} className="flex items-center gap-2 rounded-xl bg-ink-900 px-3 py-2 text-sm">
                <span className="size-3 rounded-full" style={{ background: t.color }} />
                <span className="flex-1 truncate font-semibold">
                  {t.name}
                  {t.club ? <span className="ml-1 text-[11px] text-ink-500">{tr('club')}</span> : null}
                </span>
                <span className="text-xs text-ink-500">{runners(t.members)}</span>
                {e.status === 'UPCOMING' || t.members === 0 ? (
                  <button className="text-xs text-enemy" onClick={() => removeTeam.mutate(t.id)} aria-label={tr('Remove {name}', { name: t.name })}>
                    ✕
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-ink-500">{tr('No teams: everyone runs for themselves. Add teams before runners join.')}</p>
        )}
        <div className="flex gap-2">
          <input value={teamName} onChange={(ev) => setTeamName(ev.target.value)} placeholder={tr('New team name')} maxLength={40} className={input} data-testid="team-name-input" />
          <Button
            size="sm"
            onClick={() => addTeam.mutate({ name: teamName.trim() }, { onSuccess: () => setTeamName('') })}
            disabled={teamName.trim().length < 2}
            data-testid="add-team"
          >
            {tr('Add')}
          </Button>
        </div>
        <input value={clubQ} onChange={(ev) => setClubQ(ev.target.value)} placeholder={tr('…or add a club as a team')} className={input} />
        {clubQ.trim() ? (
          <ul className="space-y-1">
            {clubs.data?.items.slice(0, 6).map((c) => (
              <li key={c.id} className="flex items-center gap-2 rounded-xl bg-ink-900 px-3 py-2 text-sm">
                <span className="size-3 rounded-full" style={{ background: c.color }} />
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-xs text-ink-500">{tr('{n} members', { n: c.stats.members })}</span>
                <Button size="sm" variant="secondary" onClick={() => addTeam.mutate({ clubId: c.id }, { onSuccess: () => setClubQ('') })}>
                  {tr('Add')}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>

      {e.teamsList.length > 1 && e.standings.individual.length ? (
        <Card className="mt-3 space-y-2">
          <div className="text-sm font-semibold">{tr('Balance teams')}</div>
          <p className="text-xs text-ink-500">{tr('Move a runner to another team. Their sectors move with them.')}</p>
          <ul className="space-y-1">
            {[...e.standings.individual]
              .sort((a, b) => (a.team?.name ?? '').localeCompare(b.team?.name ?? '') || a.user.displayName.localeCompare(b.user.displayName))
              .map((r) => (
                <UserRow key={r.user.id} u={r.user}>
                  <select
                    className="max-w-[45%] rounded-lg bg-ink-850 px-2 py-1 text-xs"
                    value={r.team?.id ?? ''}
                    onChange={(ev) => move.mutate({ userId: r.user.id, teamId: ev.target.value })}
                    disabled={move.isPending}
                    aria-label={tr('Team of {name}', { name: r.user.displayName })}
                    data-testid={`move-${r.user.displayName}`}
                  >
                    {r.team ? null : <option value="">–</option>}
                    {e.teamsList.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </UserRow>
              ))}
          </ul>
        </Card>
      ) : null}

      <ZaloBotCard e={e} />

      <Card className="mt-3">
        {confirm ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="flex-1">{tr(confirm === 'end' ? 'End now and freeze the results?' : 'Cancel this event for everyone?')}</span>
            <Button size="sm" variant="danger" onClick={() => (confirm === 'end' ? end : cancel).mutate(undefined)} data-testid="confirm-event-action">
              {tr('Yes')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>
              {tr('No')}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            {e.status === 'ACTIVE' ? (
              <Button size="sm" variant="secondary" onClick={() => setConfirm('end')} data-testid="end-event">
                {tr('End now')}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" className="text-enemy" onClick={() => setConfirm('cancel')} data-testid="cancel-event">
              {tr('Cancel event')}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

function UserRow({ u, children }: { u: PublicUser; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2 rounded-xl bg-ink-900 px-3 py-2 text-sm">
      <Avatar name={u.displayName} color={u.color} src={u.avatarUrl} size="sm" />
      <span className="min-w-0 flex-1 truncate font-semibold">{u.displayName}</span>
      {children}
    </li>
  );
}

/** Under the description: a logo grid by tier (Diamond one per row, then 2, 3, 4); a tap opens the details. */
function Sponsors({ sponsors }: { sponsors: EventDetailDto['sponsors'] }) {
  const [open, setOpen] = useState<EventDetailDto['sponsors'][number] | null>(null);
  const tiers = (['DIAMOND', 'GOLD', 'SILVER', 'PARTNER'] as const).filter((tier) => sponsors.some((s) => s.tier === tier));
  const cols = { DIAMOND: 'grid-cols-1', GOLD: 'grid-cols-2', SILVER: 'grid-cols-3', PARTNER: 'grid-cols-4' } as const;
  return (
    <section className="mt-4" data-testid="event-sponsors">
      <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">{tr('Sponsors')}</div>
      {tiers.map((tier) => (
        <div key={tier} className="mt-3" data-testid={`event-sponsors-${tier}`}>
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-ink-300">{tr(TIER_LABEL[tier])}</div>
          <ul className={`grid gap-2 ${cols[tier]}`}>
            {sponsors
              .filter((s) => s.tier === tier)
              .map((s) => (
                <li key={s.brand.id}>
                  <button type="button" onClick={() => setOpen(s)} className="block w-full text-left" aria-label={s.brand.name} data-testid={`sponsor-tile-${s.brand.name}`}>
                    <SponsorTile brand={s.brand} big={tier === 'DIAMOND'} />
                    {tier === 'DIAMOND' || tier === 'GOLD' ? <div className="mt-1 truncate text-center text-xs text-ink-300">{s.brand.name}</div> : null}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      ))}
      <Sheet open={!!open} onClose={() => setOpen(null)} label={tr('Sponsors')}>
        {open ? (
          <div className="text-center" data-testid="sponsor-sheet">
            <SponsorTile brand={open.brand} big />
            <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-ink-400">{tr(TIER_LABEL[open.tier])}</div>
            <h3 className="font-display text-2xl font-bold">{open.brand.name}</h3>
            {open.note ? <p className="mt-1 text-sm text-ink-200">{open.note}</p> : null}
            {open.brand.website ? (
              <a
                href={open.brand.website}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block rounded-full border border-ink-600 px-4 py-2 text-sm font-semibold text-volt"
                data-testid="sponsor-website"
              >
                {open.brand.website.replace(/^https?:\/\//, '').replace(/\/$/, '')} ↗
              </a>
            ) : null}
          </div>
        ) : null}
      </Sheet>
    </section>
  );
}

/** Same-sized rounded tile on the app's own surface for every logo (transparent PNGs), or the name. */
function SponsorTile({ brand, big }: { brand: { name: string; logoUrl: string | null }; big?: boolean }) {
  return (
    <div className={`relative w-full overflow-hidden rounded-xl border border-ink-700 bg-ink-850 ${big ? 'aspect-[3/1]' : 'aspect-[2/1]'}`}>
      {brand.logoUrl ? (
        <img src={brand.logoUrl} alt={brand.name} className={`absolute inset-0 h-full w-full object-contain ${big ? 'p-3' : 'p-2'}`} />
      ) : (
        <span className={`absolute inset-0 grid place-items-center px-1 text-center font-bold text-ink-100 ${big ? 'text-lg' : 'text-xs'}`}>{brand.name}</span>
      )}
    </div>
  );
}
